import {
  SearchResponse,
  BaseXlsForm,
  IOption,
  IOptionGroup,
  XLSFormSchemaByID,
  XlsForm,
} from "interfaces/dataInterfaces"
import {
  FormField,
  MappingField,
  PayloadMappingField,
  COChoice,
  BaseChoice,
  ChoiceMapping,
  HeadersSheet,
  XlsFormData,
  OptionGroup,
} from "interfaces/mappingInterfaces"
import {
  BaseXlsHH,
  IChoiceOption,
  IChoices,
  IFormType,
  IGroupForm,
  IQuestionForm,
  ListOptionGroup,
  XlsFormResult,
} from "interfaces/xlsForm"
import { blobToDownload, blobToJsonView, transformList, transformOptionGroup } from "utils/helpers"
import { householdApi } from "./householdApi"

export const xlsFormSchemas = householdApi.injectEndpoints({
  endpoints: builder => ({
    getBaseXlsForm: builder.query<
      SearchResponse<BaseXlsForm>,
      { search: string; skip: number; take: number; isArchived?: boolean }
    >({
      query: ({ search, skip, take, isArchived = false }) => ({
        url: `XlsFormSchemas/GetBaseFormsBySearch`,
        params: { search, skip, take, isArchived },
      }),
      providesTags: ["BaseXlsForm"],
    }),

    deleteXlsFormSchema: builder.mutation<void, number>({
      query: id => ({
        url: `XlsFormSchemas/Delete/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["BaseXlsForm", "XlsForm", "BaseSchemas", "LinkedSurveys", "LinkedForms"],
    }),

    getBaseSchemas: builder.query<
      { xlsFormId: number; xlsFormName: string; xlsFormSubCategoryId: number; xlsFormsIsArchived: boolean }[],
      void
    >({
      query: () => ({
        url: `XlsFormSchemas/GetBaseForms`,
      }),
      providesTags: ["BaseSchemas"],
    }),

    saveXlsForm: builder.mutation<XlsFormResult, XlsFormData>({
      query: body => {
        const {
          name,
          baseXlsFormSchemaId,
          adm0code,
          file,
          questionLabelPosition,
          choiceLabelPosition,
          xlsFormSubCategoryId,
        } = body
        const bodyFormData = new FormData()
        bodyFormData.append("name", name)
        bodyFormData.append("baseXlsFormSchemaId", baseXlsFormSchemaId?.toString() as string)
        bodyFormData.append("adm0code", adm0code.toString())
        bodyFormData.append("file", file)
        bodyFormData.append("questionLabelPosition", questionLabelPosition.toString())
        bodyFormData.append("choiceLabelPosition", choiceLabelPosition.toString())
        xlsFormSubCategoryId && bodyFormData.append("xlsFormSubCategoryId", xlsFormSubCategoryId.toString())

        return {
          url: "XlsFormSchemas/SaveForm",
          method: "POST",
          body: bodyFormData,
          formData: true,
        }
      },
      invalidatesTags: ["BaseXlsForm", "XlsForm", "BaseSchemas", "LinkedSurveys", "LinkedForms"],
    }),

    getBaseXlsFormWithGroups: builder.query<MappingField[], { xlsFormId: number }>({
      query: body => ({
        url: `XlsFormSchemas/BaseXlsFormFields`,
        method: "GET",
        params: { xlsFormId: body.xlsFormId },
      }),
      transformResponse: (response: MappingField[]): MappingField[] =>
        response?.map(item => ({
          ...item,
          groupName: item.groupName || "Other",
        })),
      providesTags: ["XlsFormMappings"],
    }),

    getXlsFormFieldsWithGroups: builder.query<FormField[], { xlsFormId: number }>({
      query: body => ({
        url: `XlsFormSchemas/FormFields`,
        method: "GET",
        params: { xlsFormId: body.xlsFormId },
      }),
      transformResponse: (response: FormField[]): FormField[] =>
        response?.map(item => ({
          ...item,
          groupName: item.groupName || "Other",
        })),
      providesTags: ["XlsFormFieldsWithGroups"],
    }),

    updateXlsFormMapping: builder.mutation<void, (MappingField | PayloadMappingField) & { xlsFormId: number }>({
      query: body => ({
        url: `XlsFormSchemas/FormMapping`,
        method: "PUT",
        params: { xlsFormId: body.xlsFormId },
        body,
      }),
      invalidatesTags: ["XlsFormMappings"],
    }),

    getChoiceList: builder.query<
      { baseListChoices: BaseChoice[]; coChoiceMappings: COChoice[] },
      { coListId: number; baseListId: number }
    >({
      query: ({ coListId, baseListId }) => ({
        url: "XlsFormSchemas/GetListChoiceMappings",
        params: { coListId, baseListId },
        method: "GET",
      }),
      providesTags: ["XlsFormQuestionList"],
    }),

    saveChoiceMapping: builder.mutation<void, { listId: number; body: ChoiceMapping }>({
      query: ({ listId, body }) => ({
        url: "XlsFormSchemas/UpdateListChoiceMappings",
        method: "PUT",
        params: { listId },
        body,
      }),
      invalidatesTags: ["XlsFormMappings"],
    }),

    uploadXlsFormFile: builder.mutation<{ surveyHeaders: HeadersSheet[]; choicesHeaders: HeadersSheet[] }, FormData>({
      query: body => ({
        url: "XlsFormSchemas/FileHeaders",
        method: "POST",
        body,
      }),
    }),

    updatePersonalInfo: builder.mutation<void, { xlsFormFieldId: number; isPersonalInfo: boolean }>({
      query: body => ({
        url: "XlsFormSchemas/UpdatePersonalInfo",
        method: "PUT",
        params: body,
      }),
      invalidatesTags: ["XlsFormMappings", "XlsFormFieldsWithGroups"],
    }),

    getDiscardedFormFields: builder.query<MappingField[], { xlsFormId: number }>({
      query: ({ xlsFormId }: { xlsFormId: number }) => ({
        url: `XlsFormSchemas/GetDiscardedMappings`,
        params: { xlsFormId },
      }),
      providesTags: ["DiscardedFormFields"],
    }),

    updateFieldDiscarded: builder.mutation<void, { xlsFormMappingId: number; discarded: boolean }>({
      query: body => ({
        url: "XlsFormSchemas/UpdateFormMappingDiscarded",
        method: "POST",
        body,
      }),
      invalidatesTags: ["DiscardedFormFields", "XlsFormMappings"],
    }),

    downloadXlsFormSchemasById: builder.mutation<void, { fileId: number; sourceName?: string; fileName?: string }>({
      query: ({ fileId, sourceName, fileName }) => ({
        url: `XlsFormSchemas/DownloadForm`,
        params: { id: fileId },
        method: "GET",
        responseHandler: async (response: Response) => {
          response.ok && blobToDownload(response, `${sourceName?.split(".xlsx_")[0] ?? fileName}.xlsx`)
        },
      }),
    }),

    getXlsForm: builder.query<
      SearchResponse<XlsForm>,
      { adm0Code: number; search: string; skip: number; take: number; isArchived?: boolean }
    >({
      query: ({ adm0Code, search, skip, take, isArchived = false }) => ({
        url: `XlsFormSchemas/Get`,
        params: { skip, take, adm0Code, search, isArchived },
      }),
      providesTags: ["XlsForm"],
    }),

    getXlsFormSchemasById: builder.query<XLSFormSchemaByID, number>({
      query: id => ({
        url: `XlsFormSchemas/Get/${id}`,
      }),
      providesTags: ["XlsFormSchema"],
    }),

    downloadJsonSchemasById: builder.mutation<void, { id: number; module: "mfi" | "rpme" | "household" }>({
      query: ({ id, module }) => ({
        url: `/Admin/Srvc/Proxy?u=apiget/XlsForms/definition?xlsFormId=${id}&xlsFormType=${module}`,
        method: "GET",
        responseHandler: async (response: Response) => {
          if (!response.ok) {
            const res = await response.json()
            return res
          }
          blobToJsonView(response)
        },
      }),
    }),

    getBaseXlsHH: builder.query<BaseXlsHH, number>({
      query: id => ({
        url: `XlsFormSchemas/GetFormBaseDetails`,
        params: { xlsFormId: id },
      }),
      providesTags: ["BaseXlsHH"],
    }),

    updateBaseXlsHH: builder.mutation<void, { xlsFormName: string; xlsFormId: number; subCategoryId?: number }>({
      query: body => ({
        url: `XlsFormSchemas/UpdateForm`,
        method: "PUT",
        params: { id: body.xlsFormId },
        body: {
          xlsFormName: body.xlsFormName,
          xlsFormId: body.xlsFormId,
          subCategoryId: body.subCategoryId,
        },
      }),
      invalidatesTags: ["BaseXlsHH", "BaseXlsForm", "XlsForm", "XlsFormSchema"],
    }),

    archiveXlsHH: builder.mutation<void, { xlsFormId: number; setArchived: boolean }>({
      query: ({ xlsFormId, setArchived }) => ({
        url: `XlsFormSchemas/UpdateArchivedStatus`,
        method: "PUT",
        params: { id: xlsFormId, setArchived },
      }),
      invalidatesTags: [
        "BaseXlsHH",
        "BaseXlsForm",
        "XlsForm",
        "XlsFormSchema",
        "FormsByCountryAndSubcategory",
        "BaseSchemas",
        "LinkedForms",
        "LinkedSurveys",
      ],
    }),

    getAllTypes: builder.query<IFormType[], number | undefined>({
      query: (id?: number) => ({
        url: `XlsFormSchemas/GetFormFieldTypes`,
        params: { xlsFormId: id },
      }),
    }),

    getFormList: builder.query<ListOptionGroup[], number>({
      query: id => ({
        url: `XlsFormSchemas/GetFormLists`,
        params: { xlsFormId: id },
      }),
      transformResponse: (response: IOption[]): ListOptionGroup[] =>
        transformList(response?.map(item => ({ ...item, listName: item.name, listId: item.value }))),
      providesTags: ["FormChoiceList"],
    }),

    getGroupList: builder.query<OptionGroup[], number>({
      query: id => ({
        url: `XlsFormSchemas/GetFormGroups`,
        params: { xlsFormId: id },
      }),
      transformResponse: (response: IOptionGroup[]): OptionGroup[] => transformOptionGroup(response),
      providesTags: ["FormGroupList"],
    }),

    saveQuestion: builder.mutation<void, { xlsFormId: number } & IQuestionForm>({
      query: body => ({
        url: `XlsFormSchemas/SaveFormField`,
        method: "POST",
        body: {
          xlsFormId: body.xlsFormId,
          xlsFormFieldTypeId: body.typeId,
          xlsFormFieldName: body.name,
          xlsFormFieldLabel: body.label,
          xlsFormGroupId: body.groupId || null,
          xlsFormListId: body.choiceListId || null,
          xlsFormListName: body.choiceList?.name || null,
          xlsFormGroup: body.group && {
            xlsFormId: body.xlsFormId,
            xlsFormGroupParentId: body.group.groupId || null,
            xlsFormGroupName: body.group.name,
            xlsFormGroupLabel: body.group.label,
            xlsFormGroupIsRepeater: body.group.isRepeat,
          },
        },
      }),
      invalidatesTags: [
        "XlsFormFieldsWithGroups",
        "FormGroupList",
        "FormChoiceList",
        "XlsFormMappings",
        "CustomQuestions",
        "ChoicesOption",
        "XlsFormSchema",
        "BaseXlsHH",
      ],
    }),

    saveGroup: builder.mutation<void, { xlsFormId: number } & IGroupForm>({
      query: body => ({
        url: `XlsFormSchemas/SaveFormGroup`,
        method: "POST",
        body: {
          xlsFormId: body.xlsFormId,
          xlsFormGroupParentId: body.groupId || null,
          xlsFormGroupName: body.name,
          xlsFormGroupLabel: body.label,
          xlsFormGroupIsRepeater: body.isRepeat,
        },
      }),
      invalidatesTags: ["XlsFormFieldsWithGroups", "FormGroupList", "XlsFormSchema", "BaseXlsHH", "XlsFormMappings"],
    }),

    getCustomQuestions: builder.query<IOption[], { xlsFormId: number }>({
      query: ({ xlsFormId }) => ({
        url: `XlsFormSchemas/GetCustomFields`,
        method: "GET",
        params: { xlsFormId },
      }),
      providesTags: ["CustomQuestions"],
    }),

    getChoicesOption: builder.query<ListOptionGroup[], { xlsFormId: number }>({
      query: ({ xlsFormId }) => ({
        url: `XlsFormSchemas/GetListChoices`,
        method: "GET",
        params: { xlsFormId },
      }),
      transformResponse: (response: IChoices[]): ListOptionGroup[] => transformList(response),
      providesTags: ["ChoicesOption"],
    }),

    saveChoice: builder.mutation<void, { xlsFormId: number } & IChoiceOption>({
      query: body => ({
        url: `XlsFormSchemas/SaveListChoice`,
        method: "POST",
        params: { xlsFormId: body.xlsFormId },
        body: {
          xlsFormListId: body.choiceListId,
          xlsFormListChoiceName: body.name,
          xlsFormListChoiceLabel: body.label,
        },
      }),
      invalidatesTags: ["ChoicesOption", "FormChoiceList", "XlsFormSchema", "BaseXlsHH"],
    }),

    getLinkedForms: builder.query<IOption[], { xlsFormId: number }>({
      query: ({ xlsFormId }) => ({
        url: "XlsFormSchemas/GetLinkedForms",
        method: "GET",
        params: { id: xlsFormId },
      }),
      providesTags: ["LinkedForms"],
    }),

    getLinkedSurveys: builder.query<IOption[], { xlsFormId: number }>({
      query: ({ xlsFormId }) => ({
        url: "XlsFormSchemas/GetLinkedSurveys",
        method: "GET",
        params: { xlsFormId },
      }),
      providesTags: ["LinkedSurveys"],
    }),

    getCategoriesField: builder.query<Record<string, number>, void>({
      query: () => ({
        url: "XlsFormSchemas/GetFormFieldTypeCategories",
        method: "GET",
      }),
      transformResponse: (response: IOption[]) => {
        return response.reduce((result, { value, name }) => {
          result[name] = value
          return result
        }, {} as Record<string, number>)
      },
    }),
  }),
})

export const {
  useGetBaseXlsFormQuery,
  useDeleteXlsFormSchemaMutation,
  useSaveXlsFormMutation,
  useGetBaseSchemasQuery,
  useGetBaseXlsFormWithGroupsQuery,
  useGetXlsFormFieldsWithGroupsQuery,
  useUpdateXlsFormMappingMutation,
  useLazyGetChoiceListQuery,
  useSaveChoiceMappingMutation,
  useUploadXlsFormFileMutation,
  useUpdatePersonalInfoMutation,
  useGetDiscardedFormFieldsQuery,
  useUpdateFieldDiscardedMutation,
  useGetXlsFormQuery,
  useGetXlsFormSchemasByIdQuery,
  useDownloadJsonSchemasByIdMutation,
  useGetBaseXlsHHQuery,
  useUpdateBaseXlsHHMutation,
  useArchiveXlsHHMutation,
  useGetAllTypesQuery,
  useGetFormListQuery,
  useGetGroupListQuery,
  useSaveQuestionMutation,
  useSaveGroupMutation,
  useGetChoicesOptionQuery,
  useSaveChoiceMutation,
  useGetCustomQuestionsQuery,
  useGetLinkedFormsQuery,
  useGetLinkedSurveysQuery,
  useGetCategoriesFieldQuery,
  useDownloadXlsFormSchemasByIdMutation,
} = xlsFormSchemas
